import { z } from 'zod';

/**
 * Product feedback schemas (#707, extended for the Apply-job companion in #951).
 *
 * First-party replacement for the PostHog-hosted Cleanup survey: a signed-in
 * user submits free text (legacy `manual` / `45s`) or a short tier-aware
 * structured survey (`apply_job`) about a named product surface. Nothing
 * Gmail-derived (subjects, senders, snippets, classification results) is ever
 * part of this payload by design - do not add a free-form context object here.
 *
 * `apply_job` payloads never carry a client-asserted plan tier: the server
 * infers the submitter's tier from their subscription status and persists it
 * as `plan_tier_at_submission`. See `docs/API_DEVELOPMENT.md` and
 * `design-artifacts/feedback-during-apply/v20260827/dev-handoff.md`.
 */

/** Pinned maximum message length. The character counter, the Zod schema, and the DB CHECK all use this. */
export const PRODUCT_FEEDBACK_MAX_MESSAGE_LENGTH = 1000;

/** How the feedback shell was opened. Mirrors the Cleanup open-trigger enum. */
export const FeedbackTriggerSchema = z.enum(['manual', '45s', 'apply', 'apply_job']);
export type FeedbackTrigger = z.infer<typeof FeedbackTriggerSchema>;

/** Viewport the feedback was submitted from. */
export const FeedbackViewportSchema = z.enum(['mobile', 'desktop']);
export type FeedbackViewport = z.infer<typeof FeedbackViewportSchema>;

/**
 * Surface identifier, e.g. `cleanup`. Format-checked rather than enumerated so a
 * future surface does not require a migration, and matches the DB CHECK.
 */
export const FeedbackSurfaceSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9_.-]*$/, 'surface must be lowercase and start with a letter');

/**
 * Allowlisted "what would make PureBox more valuable" answers for the Apply
 * job companion (#951). Everyone answers this step; 1-2 unique selections.
 */
export const FEEDBACK_VALUE_GAPS = [
  'more_accurate_suggestions',
  'clearer_suggestion_reasons',
  'more_time_saved',
  'more_control_before_apply',
  'better_automatic_cleanup',
  'something_else',
] as const;
export const ValueGapSchema = z.enum(FEEDBACK_VALUE_GAPS);
export type ValueGap = z.infer<typeof ValueGapSchema>;

/** Minimum/maximum number of unique `value_gaps` selections. */
export const FEEDBACK_VALUE_GAPS_MIN = 1;
export const FEEDBACK_VALUE_GAPS_MAX = 2;

/**
 * Allowlisted Pro-blocker answers. Only server-inferred Free users answer
 * this step; Pro/trialing users never see or submit it.
 */
export const FEEDBACK_PRO_BLOCKERS = [
  'need_more_value',
  'price_not_worth_it',
  'want_more_automation_control',
  'unclear_pro_benefits',
  'occasional_cleanup_only',
  'already_considering',
] as const;
export const ProBlockerSchema = z.enum(FEEDBACK_PRO_BLOCKERS);
export type ProBlocker = z.infer<typeof ProBlockerSchema>;

/** Server-derived plan tier at the moment of submission. Never client-supplied. */
export const PlanTierAtSubmissionSchema = z.enum(['free', 'pro']);
export type PlanTierAtSubmission = z.infer<typeof PlanTierAtSubmissionSchema>;

const uniqueValueGaps = (gaps: ValueGap[]) => new Set(gaps).size === gaps.length;

/** Legacy free-text payload: `manual` (persistent action), `45s` (timed nudge), or `apply` (post-apply prompt). Message required. */
const LegacyFeedbackRequestSchema = z.object({
  surface: FeedbackSurfaceSchema,
  trigger: z.enum(['manual', '45s', 'apply']),
  viewport: FeedbackViewportSchema,
  message: z.string().trim().min(1).max(PRODUCT_FEEDBACK_MAX_MESSAGE_LENGTH),
  app_version: z.string().max(64).optional(),
}).strict();

/**
 * Apply-job companion payload (#951): 1-2 unique value gaps required for
 * everyone, an optional Free-only pro_blocker (tier cross-check happens
 * server-side against the caller's real subscription status, never a client
 * claim), and optional free text. `.strict()` rejects any extra field (e.g. a
 * client-asserted plan tier) outright rather than silently dropping it.
 */
const ApplyJobFeedbackRequestSchema = z.object({
  surface: FeedbackSurfaceSchema,
  trigger: z.literal('apply_job'),
  viewport: FeedbackViewportSchema,
  value_gaps: z
    .array(ValueGapSchema)
    .min(FEEDBACK_VALUE_GAPS_MIN, 'Choose at least one before continuing.')
    .max(FEEDBACK_VALUE_GAPS_MAX, 'Choose up to 2.')
    .refine(uniqueValueGaps, 'value_gaps must be unique'),
  pro_blocker: ProBlockerSchema.optional(),
  message: z.string().trim().min(1).max(PRODUCT_FEEDBACK_MAX_MESSAGE_LENGTH).optional(),
  app_version: z.string().max(64).optional(),
}).strict();

export const SubmitFeedbackRequestSchema = z.discriminatedUnion('trigger', [
  LegacyFeedbackRequestSchema.extend({ trigger: z.literal('manual') }),
  LegacyFeedbackRequestSchema.extend({ trigger: z.literal('45s') }),
  LegacyFeedbackRequestSchema.extend({ trigger: z.literal('apply') }),
  ApplyJobFeedbackRequestSchema,
]);

export type SubmitFeedbackRequest = z.infer<typeof SubmitFeedbackRequestSchema>;

export const SubmitFeedbackResponseSchema = z.object({
  id: z.string().uuid(),
  created_at: z.string(),
});

export type SubmitFeedbackResponse = z.infer<typeof SubmitFeedbackResponseSchema>;
